// Authentication Store using Zustand
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { User, AuthState, Agency } from '../types';
import { authAPI } from '../services/api';

interface AuthStore extends AuthState {
  agency: Agency | null;
  token: string | null;
  setUser: (user: User | null) => void;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      agency: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (phone: string, password: string): Promise<boolean> => {
        set({ isLoading: true });

        try {
          const data = await authAPI.login(phone, password);

          set({
            user: data.user,
            agency: data.agency,
            token: data.token,
            isAuthenticated: true,
            isLoading: false
          });

          return true;
        } catch (error) {
          console.error('Login error:', error);
          set({ isLoading: false });
          return false;
        }
      },

      logout: async () => {
        try {
          await authAPI.logout();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          set({
            user: null,
            agency: null,
            token: null,
            isAuthenticated: false,
            isLoading: false
          });
        }
      },

      setUser: (user: User | null) => {
        set({
          user,
          isAuthenticated: !!user
        });
      },

      checkAuth: async () => {
        const { token } = get();

        // Check both store token and localStorage token
        const localToken = localStorage.getItem('auth_token');
        const effectiveToken = token || localToken;

        if (!effectiveToken || !authAPI.isTokenValid()) {
          // Only clear if we really have no valid token
          if (!localToken || !authAPI.isTokenValid()) {
            set({
              user: null,
              agency: null,
              token: null,
              isAuthenticated: false
            });
          }
          return;
        }

        try {
          const data = await authAPI.getMe();
          set({
            user: data.user,
            agency: data.agency,
            token: effectiveToken,
            isAuthenticated: true
          });
        } catch (error) {
          // Don't clear auth on network errors, only on actual auth failures (401)
          console.error('Auth check error:', error);
          // Only clear state if it's a 401 unauthorized error
          const errorMessage = (error as Error).message;
          if (errorMessage.includes('Unauthorized') || errorMessage.includes('Session expired')) {
            set({
              user: null,
              agency: null,
              token: null,
              isAuthenticated: false
            });
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_expires');
          }
        }
      }
    }),
    {
      name: 'bd-tour-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        agency: state.agency,
        token: state.token,
        isAuthenticated: state.isAuthenticated
      }),
      // Rehydrate on storage events (e.g., other tabs)
      onRehydrateStorage: () => (state) => {
        // Check if token exists in localStorage when rehydrating
        const localToken = localStorage.getItem('auth_token');
        if (state && localToken && authAPI.isTokenValid()) {
          state.isAuthenticated = true;
          state.token = localToken;
        }
      }
    }
  )
);
